'use strict';

import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import AuthPanel from "./AuthPanel.jsx";
import SearchPanel from "./SearchPanel.jsx";
import ScrollToTop from './ScrollToTop.jsx';
import { createSingleton } from 'tippy.js';
import useTippy from 'shared/hooks/use-tippy.js';
import tippy_defaults from './tippy-defaults.js';
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/pro-regular-svg-icons/faSearch";
import { faUserCircle } from "@fortawesome/pro-regular-svg-icons/faUserCircle";

function MenuTrigger({ content, onCreate, onDestroy, children, ...props }) {
	const [ref, portal] = useTippy(content, { onCreate, onDestroy });
	
	return (
		<Fragment>
			<span ref={ ref } { ...props }>{ children }</span>
			{ portal }
		</Fragment>
	);
}

function Menus({ menus, csrf, swiftype_engine, admin_content, article_key, current_url }) {
	const [loading, setLoading] = useState(true);
	const [user, setUser] = useState(window.logged_in_user || null);
	const [static_menus, setStaticMenus] = useState([]);
	
	const tippy_ref = useRef(null);
	const [instances, setInstances] = useState([]);
	const join_singleton = {
		onCreate: instance => setInstances(current => [...current, instance]),
		onDestroy: instance => setInstances(current => current.filter(i => i !== instance)),
	};
	
	// One shared tooltip that moves between the triggers, re-created whenever a trigger comes or goes
	useLayoutEffect(() => {
		const created = createSingleton(instances, { ...tippy_defaults, updateDuration: 300 });
		tippy_ref.current = created;
		
		return () => created.destroy();
	}, [instances]);
	
	useEffect(() => {
		window.InterNACHI.session.onGuest(() => {
			setUser(null);
			setLoading(false);
		});
		window.InterNACHI.session.onLoaded(() => {
			setUser(window.logged_in_user);
			setLoading(false);
		});
	}, []);
	
	useEffect(() => {
		setStaticMenus(menus.map((i, element) => {
			const $trigger = $(element);
			return {
				key: i,
				title: $trigger.text(),
				html: $($trigger.attr('href')).html(),
				className: $trigger.attr('class'),
				containerClassName: $trigger.parent().attr('class'),
			};
		}).toArray());
	}, [menus]);
	
	const onSearchHide = () => {
		tippy_ref.current.hide();
	};
	
	const keepOpen = () => {
		tippy_ref.current.setProps({
			delay: [0, 9999999],
		});
	};
	
	const restoreDelay = () => {
		tippy_ref.current.setProps({
			delay: tippy_defaults.delay,
		});
	};
	
	const target = (title) => {
		if ('InterNACHI' in window && 'shared' in window.InterNACHI && 'running_unit_tests' in window.InterNACHI.shared) {
			const testid = `${ title } Menu`
				.toLowerCase()
				.trim()
				.replace(/[^\w\s-]/g, '')
				.replace(/[\s_-]+/g, '-')
				.replace(/^-+|-+$/g, '');
			
			return {
				'data-testid': testid,
			};
		}
		
		return {};
	};
	
	return (
		(<Fragment>
			{ static_menus.map(({ key, title, html, className, containerClassName }) => {
				return (
					<div key={ key } className={ containerClassName }>
						<MenuTrigger
							{ ...join_singleton }
							content={ <div dangerouslySetInnerHTML={ { __html: html } } /> }
							className={ `cursor-pointer ${ className }` }
							{ ...target(title) }
						>
							{ title }
						</MenuTrigger>
					</div>
				);
			}) }
			<div className={ `ml-auto ${ loading ? 'hidden' : '' }` }>
				<MenuTrigger
					{ ...join_singleton }
					content={ <SearchPanel loading={ loading } onHide={ onSearchHide } engine={ swiftype_engine } /> }
					className="py-2 mx-2 text-base text-white no-underline cursor-pointer font-condensed"
				>
					<Icon icon={ faSearch } />
				</MenuTrigger>
			</div>
			<div className={ `${ loading ? 'hidden' : '' }` }>
				{ user && (
					<MenuTrigger
						{ ...join_singleton }
						className="py-2 mx-2 ml-4 text-base text-white no-underline cursor-pointer font-condensed"
						content={ <AuthPanel
							keepOpen={ keepOpen }
							restoreDelay={ restoreDelay }
							loading={ loading }
							user={ user }
							article_key={ article_key }
							admin_content={ admin_content }
							csrf={ csrf }
							current_url={ current_url }
						/> }
					>
						<Icon icon={ faUserCircle } />
						<span className="inline-block ml-2">{ `${ user.given_name } ${ user.family_name }` }</span>
					</MenuTrigger>
				) }
				{ ! user && (
					<a href={ route('auth.login.show') } className={ "ml-4 py-2 text-white font-condensed text-base mx-2 no-underline cursor-pointer" }>
						<Icon icon={ faUserCircle } />
						<span className="hidden ml-2 xl:inline-block">Log In</span>
					</a>
				) }
			</div>
			<ScrollToTop />
		</Fragment>)
	);
}

export default Menus;
